import { useMemo } from 'react';
import FirstDashletButton from '../FirstDashletButton';
import { DashletCard, FirstDashletCard, OverFlowContainer } from '../styles';
import {
  defaultPosition,
  getDashletGridLayout,
  getDashletKey,
  getDashletQA,
} from '../utils';
import { getDashletChrome } from 'components/DashboardGrid/hooks/dashletChrome';

export const MODES = {
  DASHBOARD: 'dashboard',
  HOMEPAGES: 'home',
  CHARTS: 'charts',
};

export const MODE_TO_BLOCK_SURFACE = {
  [MODES.HOMEPAGES]: 'homepages',
  [MODES.DASHBOARD]: 'dashboards',
  [MODES.CHARTS]: 'charts',
};

const useDashboardGridChildren = ({
  renderChild,
  forceShowUIElements,
  dashlets,
  defaultStyleSettings = {},
  hasEditPermission,
  breakpoint,
  addDashletModalTriggerProps,
  mobile,
  dateFilter,
  mode = MODES.DASHBOARD,
  filtersForCharts,
  rowHeightOverrides,
  columnCount,
}) => {
  const hasDashlets = dashlets.length > 0;

  const sortedDashletsByLayout = useMemo(() => {
    return (
      dashlets?.sort((a, b) => {
        if (a.layout?.y > b.layout?.y) {
          return 1;
        } else if (a.layout?.y < b.layout?.y) {
          return -1;
        } else {
          if (a.layout?.x > b.layout?.x) {
            return 1;
          } else if (a.layout?.x < b.layout?.x) {
            return -1;
          }
          return 0;
        }
      }) ?? []
    );
  }, [dashlets]);

  const children = useMemo(() => {
    if (!hasDashlets && !hasEditPermission) {
      return [];
    }
    if (!hasDashlets && mobile && mode === MODES.CHARTS) {
      return [];
    }
    if (!hasDashlets) {
      return [
        <FirstDashletCard
          data-grid={defaultPosition}
          key={`add-new:${breakpoint}`}
        >
          <FirstDashletButton
            triggerAddDashletProps={addDashletModalTriggerProps}
            mode={mode}
          />
        </FirstDashletCard>,
      ];
    }
    return sortedDashletsByLayout.map((dashlet, index) => {
      const forceShowUI = forceShowUIElements.includes(dashlet.id);
      const {
        htmlBlockConfig,
        hasHeaderOverlay,
        dashletStyleConfig,
        borderRadius,
        titleBackgroundColor,
        backgroundColor,
        hasShadow,
      } = getDashletChrome(dashlet, defaultStyleSettings);
      return (
        <DashletCard
          data-grid={getDashletGridLayout(
            dashlet,
            rowHeightOverrides,
            columnCount
          )}
          {...getDashletQA(dashlet, rowHeightOverrides)}
          // Stable hook for finding a dashlet's card, e.g. the AI panel's capture
          data-dashlet-id={dashlet.id}
          key={
            getDashletKey(dashlet, breakpoint, dateFilter, filtersForCharts) +
            String(rowHeightOverrides?.[dashlet.id])
          }
          customStyles={{
            titleBackgroundColor,
            dropShadow: dashletStyleConfig?.dropShadow,
            borderColor: dashletStyleConfig?.borderColor,
            borderWeight: dashletStyleConfig?.borderWeight,
            titleAllCaps: dashletStyleConfig?.titleAllCaps,
            titleLineHeight: dashletStyleConfig?.titleLineHeight,
            titleFontSize: dashletStyleConfig?.titleFontSize,
            boldTitle: dashletStyleConfig?.boldTitle,
            fontFamily: dashletStyleConfig?.fontFamily,
            italicTitle: dashletStyleConfig?.italicTitle,
            borderRadius,
            bottomRightBorderRadius:
              dashletStyleConfig?.bottomRightBorderRadius,
          }}
          forceShowUI={forceShowUI}
          alwaysShowMenu={mobile}
          data-qa-has-controls={hasEditPermission}
          backgroundColor={backgroundColor}
          hasShadow={hasShadow}
          isHTML={hasHeaderOverlay}
          disableResize={dashlet.disableResize}
        >
          <OverFlowContainer>
            {renderChild({
              dashlet,
              errorPadding: Boolean(htmlBlockConfig),
              index,
            })}
          </OverFlowContainer>
        </DashletCard>
      );
    });
  }, [
    addDashletModalTriggerProps,
    breakpoint,
    sortedDashletsByLayout,
    dateFilter,
    forceShowUIElements,
    hasDashlets,
    defaultStyleSettings,
    hasEditPermission,
    mobile,
    renderChild,
    mode,
    filtersForCharts,
    rowHeightOverrides,
    columnCount,
  ]);

  return children;
};

export default useDashboardGridChildren;
